import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Search } from "lucide-react";

/**
 * SearchableSelect — lightweight typeahead dropdown used by the HR Asset
 * forms (Employee ID, Asset ID, Branch pickers).
 *
 * Props:
 *   value:     the currently-selected option value (string) or ""
 *   onChange:  (nextValue) => void
 *   options:   [{ value, label, sub? }] — `sub` renders as a muted subtitle
 *   placeholder, disabled, required, testid, className
 *
 * Behaviour: filters `options` case-insensitively on both `label` and `sub`
 * as the user types. Keyboard: ArrowUp/Down/Enter/Escape. Click-outside to
 * close. Clear button appears when a value is selected (unless required).
 */
export default function SearchableSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  required = false,
  testid,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoverIdx, setHoverIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 20); }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(q) ||
      (o.sub || "").toLowerCase().includes(q) ||
      String(o.value || "").toLowerCase().includes(q)
    );
  }, [query, options]);

  const selected = options.find((o) => o.value === value);

  const pick = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setQuery("");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHoverIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[hoverIdx]) pick(filtered[hoverIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={`iz-input w-full flex items-center justify-between text-left ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span className={selected ? "text-white truncate" : "text-gray-500"}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && !required && !disabled && (
            <span role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                  className="p-0.5 rounded hover:bg-[#262626] text-gray-500 hover:text-gray-300 cursor-pointer">
              <X className="w-3 h-3"/>
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}/>
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#0a0a0a] border border-[#262626] rounded-md shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#1a1a1a] flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-gray-500 flex-shrink-0"/>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHoverIdx(0); }}
              onKeyDown={onKey}
              placeholder="Type to search…"
              className="w-full bg-transparent text-sm text-white placeholder:text-gray-600 outline-none"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <ul className="max-h-64 overflow-y-auto" data-testid={testid ? `${testid}-list` : undefined}>
            {filtered.length === 0 && (
              <li className="p-3 text-xs font-mono text-gray-500 text-center italic">No matches</li>
            )}
            {filtered.map((opt, i) => (
              <li key={opt.value}
                  onMouseEnter={() => setHoverIdx(i)}
                  onClick={() => pick(opt)}
                  className={`px-3 py-2 cursor-pointer border-l-2 transition-colors ${
                    i === hoverIdx ? "border-[#d4af37] bg-[#d4af37]/5" : "border-transparent"
                  } ${opt.value === value ? "bg-[#1a1a1a]" : ""}`}>
                <p className="text-sm text-white truncate">{opt.label}</p>
                {opt.sub && <p className="text-[10px] font-mono text-gray-500 truncate">{opt.sub}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
